import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy, inject } from '@angular/core';

@Directive({ selector: '[subscriptionDialogFocus]', standalone: true })
export class SubscriptionDialogFocusDirective implements AfterViewInit, OnDestroy {
  private readonly element: HTMLElement = inject(ElementRef).nativeElement;
  private readonly origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  private frame = 0;

  private controls(): HTMLElement[] {
    return Array.from(this.element.querySelectorAll<HTMLElement>('button, a[href], input, select, textarea, [tabindex]'))
      .filter(el => !el.hasAttribute('disabled') && el.tabIndex >= 0 && el.getClientRects().length > 0);
  }

  ngAfterViewInit(): void {
    this.frame = requestAnimationFrame(() => (this.controls()[0] ?? this.element).focus());
  }

  @HostListener('keydown', ['$event'])
  trapTab(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    const controls = this.controls();
    const index = controls.indexOf(document.activeElement as HTMLElement);
    event.preventDefault();
    if (!controls.length) { this.element.focus(); return; }
    controls[(index + (event.shiftKey ? -1 : 1) + controls.length) % controls.length].focus();
  }

  @HostListener('document:focusin', ['$event'])
  keepFocusInside(event: FocusEvent): void {
    if (event.target instanceof Node && !this.element.contains(event.target)) {
      (this.controls()[0] ?? this.element).focus();
    }
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.frame);
    // Saving can close the dialog while its opener is disabled during list refresh.
    const restore = (remaining: number) => {
      if (!this.origin?.isConnected) return;
      if (this.origin.matches(':disabled')) {
        if (remaining > 0) setTimeout(() => restore(remaining - 1), 50);
        return;
      }
      this.origin.focus();
    };
    queueMicrotask(() => restore(320));
  }
}
